import { Component, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { Observable, Subscription, forkJoin, of } from "rxjs";
import { tap, catchError } from "rxjs/operators";
//import { GridOptions, GridApi, ColDef, RedrawRowsParams } from '@ag-grid-enterprise/all-modules'
import { GridOptions, GridApi, ColDef, RedrawRowsParams } from "ag-grid-community"

import { AgGridBooleanFilterComponent } from "../../../common/components/ag-grid/filters/boolean-filter.component";
import { IconActionRendererComponent } from "../../../common/components/ag-grid/icon-action-renderer.component";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { IssuesService, IssueSearchSpec, OrderParameters } from "../../../common/services/issues.service";
import { Issue, IssueType, IssuesStatus } from "../../../common/models/issue.model";
import { TabComponent } from "../../../common/components/tabs/tab.component";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { OrganizationService } from "../../../common/services/organization.service";
import { Organization, OperationType } from "../../../common/models/organization.model";
import { ContactService } from "../../../admin/services/contacts.service";
import { Contact } from "../../../admin/models/contact.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { LoaderService } from "../../../common/services/loader.service";

@Component({
    selector: 'view-issues',
    templateUrl: './view-issues.component.html',
    styleUrls: ['./view-issues.component.scss'],
    standalone: false
})
export class ViewIssuesComponent implements OnInit {

    subheader: SubHeader;

    contacts: Contact[];

	closedGridOptions: GridOptions;
	closedGridApi?: GridApi;
	contestedGridOptions: GridOptions;
	contestedGridApi?: GridApi;
	escalatedGridOptions: GridOptions;
	escalatedGridApi?: GridApi;
	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
	progressGridOptions: GridOptions;
	progressGridApi?: GridApi;

    dataPending: Observable<Issue[]>;
    dataProgress: Observable<Issue[]>;
    dataEscalated: Observable<Issue[]>;
    dataContested: Observable<Issue[]>;
    dataClosed: Observable<Issue[]>;


    errorDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return true; })]);

    organizations: Organization[];
    issueTypes: IssueType[] = [];
    selectedTabName: string = "New";
    subscriptions: Subscription = new Subscription();

    overlayLoadingTemplate: string = '<span class="ag-overlay-loading-center">Please wait ...</span>';

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }

    apiColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "API #",
        headerTooltip: "API #",
        field: "wellId",
        valueGetter: this.formatter.formatPotentiallyBlankField("wellId", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellId", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 125,
        maxWidth: 150,
        cellClass: "stringType"
    } as ColDef;

    assigneeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Assignee",
        headerTooltip: "Assignee",
        field: "assignee",
        valueGetter: this.formatAssignee("assignee"),
        tooltipValueGetter: this.formatAssignee("assignee"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
        maxWidth: 200
    } as ColDef;

    complianceNotice1Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Compliance Notice 1 Received Date",
        headerTooltip: "Compliance Notice 1 Received Date",
        field: "complianceNotice1ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("complianceNotice1ReceivedDate"),
        valueGetter: this.formatter.formatDate("complianceNotice1ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    complianceNotice2Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Compliance Notice 2 Received Date",
        headerTooltip: "Compliance Notice 2 REceived Date",
        field: "complianceNotice2ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("complianceNotice2ReceivedDate"),
        valueGetter: this.formatter.formatDate("complianceNotice2ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    closedDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Closed",
        headerTooltip: "Date Closed",
        field: "resolutionDate",
        tooltipValueGetter: this.formatter.formatDate("resolutionDate"),
        valueGetter: this.formatter.formatDate("resolutionDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 135,
        maxWidth: 135,
        pinned: 'left'
    } as ColDef;

    createdDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Created",
        headerTooltip: "Date Created",
        field: "creationDate",
        tooltipValueGetter: this.formatter.formatDate("creationDate"),
        valueGetter: this.formatter.formatDate("creationDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 110,
        maxWidth: 110,
        pinned: 'left'
    } as ColDef;

    descriptionColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Description",
        headerTooltip: "Issue Description",
        field: "description",
        tooltipField: "description",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 225
    } as ColDef;

    dueDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Due Date",
        headerTooltip: "Due Date",
        field: "dateDue",
        tooltipValueGetter: this.formatter.formatDate("dateDue"),
        valueGetter: this.formatter.formatDate("dateDue"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    editColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "",
        headerTooltip: "",
        field: "",
        filter: false,
        maxWidth: 60,
        minWidth: 60,
        cellStyle: { 'text-align': 'center', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj) => this.editIssue(obj),
                    label: 'Edit Issue',
                    icon: 'edit'
                }
            ]
        },
        pinned: 'left'
    } as ColDef;


    approvalEditColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "",
        headerTooltip: "",
        field: "",
        filter: false,
        maxWidth: 60,
        minWidth: 60,
        cellStyle: { 'text-align': 'center', 'line-height': 1.75 },
        cellRenderer: IconActionRendererComponent,
        cellRendererParams: {
            buttons: [
                {
                    action: (obj) => this.approveIssue(obj),
                    label: 'Edit Issue',
                    icon: 'edit'
                }
            ]
        },
        pinned: 'left'
    } as ColDef;


    issueNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Number",
        headerTooltip: "Issue Number",
        field: "identifier",
        tooltipField: "identifier",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 120,
        pinned: 'left'
    } as ColDef;

    issueTypeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Code",
        headerTooltip: "Issue Code",
        field: "typeId",
        tooltipValueGetter: this.formatIssueType("typeId"),        
        minWidth: 125,
        maxWidth: 125,
        pinned: 'left'
    } as ColDef;

    letter1Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Letter 1 Received Date",
        headerTooltip: "Letter 1 Received Date",
        field: "letter1ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("letter1ReceivedDate"),
        valueGetter: this.formatter.formatDate("letter1ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    letter2Column: ColDef = {
        headerClass: "wrapped",
        headerName: "Letter 2 Received Date",
        headerTooltip: "Letter 2 REceived Date",
        field: "letter2ReceivedDate",
        tooltipValueGetter: this.formatter.formatDate("letter2ReceivedDate"),
        valueGetter: this.formatter.formatDate("letter2ReceivedDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    nextActionDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date of Next Action",
        headerTooltip: "Date of Next Action",
        field: "dateOfNextAction",
        tooltipValueGetter: this.formatter.formatDate("dateOfNextAction"),
        valueGetter: this.formatter.formatDate("dateOfNextAction"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    ownerNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Name At Time of Issue",
        headerTooltip: "Owner Name At Time of Issue",
        field: "organizationNameAtTimeOfIssue",
        valueGetter: this.formatter.formatPotentiallyBlankField("organizationNameAtTimeOfIssue", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("organizationNameAtTimeOfIssue", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    ownerNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Number At Time of Issue",
        headerTooltip: "Owner Number At Time of Issue",
        field: "organizationId",
        valueGetter: this.formatter.formatPotentiallyBlankField("organizationId", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("organizationId", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    programAreaColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Program Area",
        headerTooltip: "Program Area",
        field: "issueOwner",
        tooltipField: "issueOwner",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    registeredColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Registered in RICS?",
        headerTooltip: "Registered in RICS?",
        tooltipValueGetter: this.formatRegistered("organizationId"),
        valueGetter: this.formatRegistered("organizationId"),
        filter: false,
        sortable: false,
        suppressMenuHeaderButtons: true,
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    ownerTypeColumn: ColDef = {
        headerName: "Owner Type",
        headerTooltip: "Owner Type",
        tooltipValueGetter: this.formatOwnerType("organizationId"),
        valueGetter: this.formatOwnerType("organizationId"),
        filter: false,
        sortable: false,
        suppressMenuHeaderButtons: true,
        minWidth: 110
    } as ColDef;

    pendingColumns: ColDef[] = [
        this.editColumn,
        this.createdDateColumn,
        this.issueNumberColumn,
        this.issueTypeColumn,
        this.descriptionColumn,
        this.dueDateColumn,
        this.ownerNameColumn,
        this.ownerNumberColumn,
        this.ownerTypeColumn,
        this.apiColumn,
        this.registeredColumn,
        this.programAreaColumn
    ];

    progressColumns: ColDef[] = [
        this.editColumn,
        this.createdDateColumn,
        this.issueNumberColumn,
        this.issueTypeColumn,
        this.descriptionColumn,
        this.ownerNumberColumn,
        this.ownerNameColumn,
        this.ownerTypeColumn,
        this.apiColumn,
        this.assigneeColumn,
        this.nextActionDateColumn,
        this.dueDateColumn,
        this.letter1Column,
        this.letter2Column,
        this.complianceNotice1Column,
        this.complianceNotice2Column,
        this.registeredColumn,
        this.programAreaColumn
    ];

    escalationColumns: ColDef[] = [
        this.approvalEditColumn,
        this.createdDateColumn,
        this.issueNumberColumn,
        this.issueTypeColumn,
        this.descriptionColumn,
        this.ownerNumberColumn,
        this.ownerNameColumn,
        this.ownerTypeColumn,
        this.apiColumn,
        this.assigneeColumn,
        this.nextActionDateColumn,
        this.dueDateColumn,
        this.letter1Column,
        this.letter2Column,
        this.complianceNotice1Column,
        this.complianceNotice2Column,
        this.registeredColumn,
        this.programAreaColumn
    ];

    contestedColumns: ColDef[] = [
        this.editColumn,
        this.createdDateColumn,
        this.issueNumberColumn,
        this.issueTypeColumn,
        this.descriptionColumn,
        this.ownerNumberColumn,
        this.ownerNumberColumn,
        this.ownerTypeColumn,
        this.apiColumn,
        this.assigneeColumn,
        this.nextActionDateColumn,
        this.dueDateColumn,
        this.letter1Column,
        this.letter2Column,
        this.complianceNotice1Column,
        this.complianceNotice2Column,
        this.registeredColumn,
        this.programAreaColumn
    ];

    closedColumns: ColDef[] = [
        this.editColumn,
        this.createdDateColumn,
        this.closedDateColumn,       
        this.issueNumberColumn,
        this.issueTypeColumn,
        this.descriptionColumn,
        this.ownerNumberColumn,
        this.ownerNameColumn,
        this.ownerTypeColumn,
        this.apiColumn,
        this.dueDateColumn,        
        this.letter1Column,
        this.letter2Column,
        this.complianceNotice1Column,
        this.complianceNotice2Column,
        this.registeredColumn,
        this.programAreaColumn,
    ];


    

    constructor(private formatter: GridFormatterService, private issuesService: IssuesService, private dialogService: DynamicDialogService, private organizationService: OrganizationService, private contactService: ContactService,
        private subheaderService: SubHeaderService, private loader: LoaderService, private router: Router) { }

    ngOnInit() {
        this.subheader = new SubHeader({ title: "View All Issues", buttons: [{ name: "Export Current Tab", method: () => this.exportTab() }] });
        this.subheaderService.updateSubHeader(this.subheader);
        this.defineGridOptions();



        const typeObs$ = this.issuesService.getAllIssueTypes().pipe(
            tap((types: IssueType[]) => {
                this.issueTypes = types;
            })
        );

        const orgObs$ = this.organizationService.getAllExternalOrganizations().pipe(
            tap((orgs: Organization[]) => {
                this.organizations = orgs;
            })
        );

        const contact$ = this.contactService.getContactsByOrganization("0002").pipe(
            tap((contacts: Contact[]) => {
                this.contacts = contacts;
            }));

        const subs$ = forkJoin(typeObs$, orgObs$, contact$).pipe()
            .subscribe(
                (data: any[]) => {
                    this.redrawRows();
                },
                (err) => {
                    console.error(err);
                    this.showError("ERROR Fetching Issue Types");
                }
            );
        this.subscriptions.add(subs$);

        this.loadData();
    }

    approveIssue(data: any): void {
        this.navigate(`/issues/approve-escalation/${data.id}`, true);
    }

    defineGridOptions(): void {
        this.pendingGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.pendingColumns, false, true, true, true);
        this.pendingGridOptions.headerHeight = 50;
		this.pendingGridOptions.overlayLoadingTemplate = this.overlayLoadingTemplate;
		this.pendingGridOptions.onGridReady = (params) => { this.pendingGridApi = params.api; };
        this.progressGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.progressColumns, false, true, true, true);
        this.progressGridOptions.headerHeight = 50;
		this.progressGridOptions.overlayLoadingTemplate = this.overlayLoadingTemplate;
		this.progressGridOptions.onGridReady = (params) => { this.progressGridApi = params.api; };
        this.escalatedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.escalationColumns, false, true, true, true);
        this.escalatedGridOptions.headerHeight = 50;
		this.escalatedGridOptions.overlayLoadingTemplate = this.overlayLoadingTemplate;
		this.escalatedGridOptions.onGridReady = (params) => { this.escalatedGridApi = params.api; };
        this.contestedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.contestedColumns, false, true, true, true);
        this.contestedGridOptions.headerHeight = 50;
		this.contestedGridOptions.overlayLoadingTemplate = this.overlayLoadingTemplate;
		this.contestedGridOptions.onGridReady = (params) => { this.contestedGridApi = params.api; };
        this.closedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.closedColumns, false, true, true, true);
        this.closedGridOptions.headerHeight = 50;
		this.closedGridOptions.overlayLoadingTemplate = this.overlayLoadingTemplate;
		this.closedGridOptions.onGridReady = (params) => { this.closedGridApi = params.api; };
    }

    editIssue(data: any): void {
        this.navigate(`/issues/review-issue/${data.id}`, true);
    }

    exportTab(): void {
        this.loader.toggleLoader(true);
        let api: GridApi;
        let now = new Date();
        let fileName: string = "";
        switch (this.selectedTabName) {
            case "New":
                api = this.pendingGridApi;
                fileName = `New_Issues_${now.toLocaleString()}`;
                break;
            case "In Progress":
                api = this.progressGridApi;
                fileName = `InProgress_Issues_${now.toLocaleString()}`;
                break;
            case "Escalation Review":
                api = this.escalatedGridApi;
                fileName = `Escalated_Issues_${now.toLocaleString()}`;
                break;
            case "Contested":
                api = this.contestedGridApi;
                fileName = `Contested_Issues_${now.toLocaleString()}`;
                break;
            case "Closed":
                api = this.closedGridApi;
                fileName = `Closed_Issues_${now.toLocaleString()}`;
                break;
        }        
        setTimeout(() => { this.loader.toggleLoader(false); }, 5000);
        this.formatter.exportDataToExcel(fileName, "Issues", api, false);        
    }

    formatAssignee(field: string): any {
        return (obj) => {
            let assignee = "N/A";
            if (obj.data != null && obj.data[field] != null) {
                assignee = obj.data[field];
                if (this.contacts != null) {
                    const contact = this.contacts.find(c => c.id === assignee);
                    if (contact != null) assignee = contact.displayName;
                }
            }
            return assignee;
        }
    }

    formatIssueType(field: string): any {
        return (obj) => {
            let returnValue = "N/A";
            if (obj.data && obj.data[field] != undefined && obj.data[field] != null) {
                let matchingIssueTypes = this.issueTypes.filter(a => a.id == obj.data[field]);
                if (matchingIssueTypes && matchingIssueTypes.length > 0)
                    returnValue = "(" + matchingIssueTypes[0].id + ") " + matchingIssueTypes[0].description;
            }

            return returnValue;
        }
    }

    formatOwnerType(field: string): any {
        return (obj) => {
            let returnValue: string = "";
            if (this.organizations == null) {
                returnValue = "Loading";
            } else if (obj.data != null && obj.data[field] != null) {
                const value = obj.data[field].toString();
                let org: Organization;
                if (value.startsWith("ON")) {
                    org = this.organizations.find(o => o.id === value);
                } else {
                    let hist = this.organizations.filter(o => o.historicalOwnerNumber === value);
                    if (hist.length === 0) {
                        hist = this.organizations.filter(o => o.id === value);
                    }
                    if (hist.length > 0) {
                        org = hist.find(h => h.hasRegistered === true);
                        if (org == null) org = hist[0];
                    }
                }
                if (org != null) {
                    let wellOwner: boolean = ((org.operationType & OperationType.Operator) !== 0 || (org.operationType & OperationType.InjectionOperator) !== 0);
                    let brineHauler: boolean = (org.operationType & OperationType.BrineHauler) !== 0;
                    if (brineHauler && wellOwner) {
                        returnValue = "Brine Hauler, Well Owner";
                    } else if (brineHauler) {
                        returnValue = "Brine Hauler";
                    } else if (wellOwner) {
                        returnValue = "Well Owner";
                    }
                }
            }
            return returnValue;
        }
    }

    formatRegistered(field: string): any {
        return (obj) => {
            let returnValue = "No";
            if (this.organizations == null) {
                returnValue = "Loading";
            } else if (obj.data && obj.data[field] != null) {
                const value = obj.data[field].toString();
                let found: boolean = false;
                if (value.startsWith("ON")) {
                    const org = this.organizations.find(o => o.id === value);
                    returnValue = (org != null && org.hasRegistered) ? "Yes" : "No";
                    found = org != null;
                } else {
                    let hist = this.organizations.filter(o => o.historicalOwnerNumber === value);
                    if (hist.length === 0) {
                        hist = this.organizations.filter(o => o.id === value);
                        found = true;
                    }
                    returnValue = (hist.length > 0 && hist.find(h => h.hasRegistered === true)) ? "Yes" : "No";
                }
            }
            return returnValue;
        }
    }

    loadData(): void {
        const spec = {
            loadChildren: false,
            statuses: [],
            ordering: {
                ascending: false,
                field: "creationDate"
            } as OrderParameters,
        } as IssueSearchSpec;
		let options: GridOptions;
		let api: GridApi;
        switch (this.selectedTabName) {
            case "New":
                spec.statuses.push(IssuesStatus.Pending);
				options = this.pendingGridOptions;
				api = this.pendingGridApi;
                break;
            case "In Progress":
                spec.statuses = [IssuesStatus.Validated, IssuesStatus.Contested, IssuesStatus.Escalated];
				options = this.progressGridOptions;
				api = this.progressGridApi;
                break;
            case "Escalation Review":
                spec.statuses.push(IssuesStatus.ChiefsOrderApproval);
				options = this.escalatedGridOptions;
				api = this.escalatedGridApi;
                break;
            case "Contested":
                spec.statuses.push(IssuesStatus.Contested);
				options = this.contestedGridOptions;
				api = this.contestedGridApi;
                break;
            case "Closed":
				options = this.closedGridOptions;
				api = this.closedGridApi;
                spec.statuses.push(IssuesStatus.Closed);
                break;
        }
        if (options != null && api != null) {
            options.suppressNoRowsOverlay = true;
            setTimeout(() => {
                api.setGridOption("loading", true);
            }, 100);
        }
        let obs$ = this.issuesService.getIssues(spec).pipe(
            catchError((err) => {
                this.showError(err);
                return of([]);
            }),
            tap((data: Issue[]) => {
                if (data == null || data.length === 0) {
                    options.suppressNoRowsOverlay = false;
                } else {
                    api.hideOverlay();
                }
                this.sizeGrids()
            }));
        switch (this.selectedTabName) {
            case "New":
                this.dataPending = obs$;
                break;
            case "In Progress":
                this.dataProgress = obs$;
                break;
            case "Escalation Review":
                this.dataEscalated = obs$;
                break;
            case "Contested":
                this.dataContested = obs$;
                break;
            case "Closed":
                this.dataClosed = obs$;
                break;
        }
    }

    navigate(target: string, openInNewWindow: boolean) {
        if (openInNewWindow) {
            const url = this.router.serializeUrl(this.router.createUrlTree([target]));
            window.open(url, '_blank');
        } else {
            this.router.navigate([target]);
        }
    }

    onTabClicked(tab: TabComponent): void {
        this.selectedTabName = tab.title;
        this.loadData();
    }

    redrawRows(): void {
        let api: GridApi;
        switch (this.selectedTabName) {
            case "New":
                if (this.pendingGridApi) {
                    api = this.pendingGridApi;
                }
                break;
            case "In Progress":
                if (this.progressGridApi) {
                    api = this.progressGridApi;
                }
                break;
            case "Escalation Review":
                if (this.escalatedGridApi) {
                    api = this.escalatedGridApi;
                }
                break;
            case "Contested":
                if (this.contestedGridApi) {
                    api = this.contestedGridApi;
                }
                break;
            case "Closed":
                if (this.closedGridApi) {
                    api = this.closedGridApi
                }
                break;
        }
        if (api != null) {
            api.setGridOption("loading", false);
            var params = <RedrawRowsParams>[];
            api.redrawRows(params);
            api.sizeColumnsToFit();
        }
    }

    showError(msg: string): void {
        this.errorDialog.message = msg;
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    sizeGrids(): void {
        switch (this.selectedTabName) {
            case "New":
                if (this.pendingGridApi) this.pendingGridApi.sizeColumnsToFit();
                break;
            case "In Progress":
                if (this.progressGridApi) this.progressGridApi.sizeColumnsToFit();
                break;
            case "Escalation Review":
                if (this.escalatedGridApi) this.escalatedGridApi.sizeColumnsToFit();
                break;
            case "Contested":
                if (this.contestedGridApi) this.contestedGridApi.sizeColumnsToFit();
                break;
            case "Closed":
                if (this.closedGridApi) this.closedGridApi.sizeColumnsToFit();
                break;
        }
    }


}
